import { useState, useEffect, useMemo, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/axios";
import {
  ExtractedContract,
} from "../../types/contract";
import { DirtyStateContext } from "../../contexts/DirtyStateContext";
import { useAllData } from "../../contexts/AllDataContext";
import { useFilter } from "./useFilter";
import { useBuilding } from "./useBuilding";
import { useDirtyState } from "./useDirtyState";
import { useConfirmSave } from "./useConfirmSave";
import { useContractFilterOptions } from "../../hooks/useContractFilterOptions";

export const useConfirmPage = () => {
  // 1. 外部コンテキスト
  const {
    contracts: originalContracts,
    loading: contextLoading,
    error: contextError,
    fetchData,
    setContracts: setGlobalContracts,
    // ページネーション機能
    hasNextPage,
    fetchNextPage,
    pagination,
    dataMode,
    mode,
    searchData,
    clearSearch,
    itemsPerPage,
    setItemsPerPage,
    sortOrder: globalSortOrder,
    setSortOrder: setGlobalSortOrder,
    hasInitialLoaded,
  } = useAllData();

  const { setIsDirty } = useContext(DirtyStateContext)!;
  const navigate = useNavigate();

  // 2. ページ全体の状態
  const [isTrashView, setIsTrashView] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity?: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
  });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [sourceMenuAnchorEl, setSourceMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const pageScrollRef = useRef<HTMLDivElement>(null);
  const detailContentRef = useRef<HTMLDivElement>(null);

  // ソート順は AllDataContext で管理
  const sortOrder = globalSortOrder;

  // 3. サブフック
  const filter = useFilter({ isTrashView, searchData, clearSearch });

  const building = useBuilding({
    originalContracts,
    isTrashView,
    dataMode,
    sortOrder,
    itemsPerPage,
    pagination,
    propertyFilter: filter.propertyFilter,
    roomFilter: filter.roomFilter,
    lesseeFilter: filter.lesseeFilter,
    pageScrollRef,
    detailContentRef,
  });

  const { propertyOptions, roomOptions, lesseeOptions } = useContractFilterOptions(
    building.editableContracts,
    filter.propertyFilter,
    filter.roomFilter,
    filter.lesseeFilter
  );

  const dirtyState = useDirtyState({
    selectedContract: building.selectedContract,
    editableContracts: building.editableContracts,
    setEditableContracts: building.setEditableContracts,
    setIsDirty,
  });

  const confirmSave = useConfirmSave({
    editableContracts: building.editableContracts,
    setEditableContracts: building.setEditableContracts,
    setSelectedContract: building.setSelectedContract,
    setGlobalContracts,
    originalContracts,
    originalSelectedContract: dirtyState.originalSelectedContract,
    selectedEditableContract: dirtyState.selectedEditableContract,
    changedContractIds: dirtyState.changedContractIds,
    setChangedContractIds: dirtyState.setChangedContractIds,
    setSnackbar,
    exitEditMode: dirtyState.exitEditMode,
  });

  // 4. ブリッジハンドラー
  const handleSelectItem = (item: ExtractedContract) => {
    dirtyState.confirmAndNavigate(() => building.setSelectedContract(item));
  };

  // 5. ページ全体の effect / useMemo

  // 一度でもデータを取得したかを記録
  useEffect(() => {
    if (building.editableContracts.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [building.editableContracts.length]);

  // データがクリアされて再ロードが始まったらリセット（会社切り替え等）
  useEffect(() => {
    if (originalContracts.length === 0 && contextLoading) {
      setHasLoadedOnce(false);
    }
  }, [originalContracts.length, contextLoading]);

  // ゴミ箱切替 effect（fetchTrashData / fetchData 呼び出し）
  useEffect(() => {
    if (isTrashView) {
      confirmSave.fetchTrashData();
    } else {
      if (originalContracts.length === 0 && !hasInitialLoaded && !contextLoading) {
        fetchData();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrashView, fetchData, originalContracts.length, hasInitialLoaded, contextLoading]);

  // sourceFiles useMemo
  const sourceFiles = useMemo(() => {
    if (!building.selectedContract) return [];
    // Get source files only for the specific selected contract (by ID)
    const selectedContractData = building.editableContracts.find(
      (c) => c.id === building.selectedContract!.id
    );
    if (!selectedContractData || !selectedContractData.filename) return [];

    const files = selectedContractData.filename
      .split(",")
      .map((f) => f.trim())
      .filter((f) => f);
    return [...new Set(files)];
  }, [building.editableContracts, building.selectedContract]);

  // Snackbarが表示された際に、ページを最上部までスクロールする
  useEffect(() => {
    if (snackbar.open && pageScrollRef.current) {
      pageScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [snackbar.open, pageScrollRef]);

  // ページ内ハンドラー（useConfirmPage に残るもの）
  const handleOpenFile = async (originalFilename: string) => {
    if (!originalFilename || !building.selectedContract) return;

    try {
      const selectedContractData = building.editableContracts.find(
        (c) => c.id === building.selectedContract!.id
      );

      if (!selectedContractData) {
        console.error("Contract data not found");
        alert("契約データが見つかりません");
        return;
      }

      let fileToFetch: string;

      if (!selectedContractData.filename_uuid_map) {
        // Legacy data → use original filename
        fileToFetch = originalFilename;
      } else {
        // UUID data → get UUID from mapping
        const uuidMap = JSON.parse(selectedContractData.filename_uuid_map);
        fileToFetch = uuidMap[originalFilename];
        if (!fileToFetch) {
          alert("ファイルマッピングが見つかりません");
          return;
        }
      }

      const response = await apiClient.get(`/api/files/confirmed/${fileToFetch}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error("Could not open file:", err);
      alert("ファイルを開けませんでした");
    }
  };

  const handleOpenSourceMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSourceMenuAnchorEl(event.currentTarget);
  };

  const handleCloseSourceMenu = () => {
    setSourceMenuAnchorEl(null);
  };

  // 6. loading / error 計算
  const loading = contextLoading || confirmSave.operationLoading;
  const error = isTrashView ? confirmSave.trashError : contextError;

  // 7. return（全サブフックの戻り値をスプレッドして展開）
  return {
    ...filter,
    ...building,
    propertyOptions,
    roomOptions,
    lesseeOptions,
    ...dirtyState,
    ...confirmSave,
    isTrashView,
    setIsTrashView,
    snackbar,
    setSnackbar,
    hasLoadedOnce,
    sourceMenuAnchorEl,
    setSourceMenuAnchorEl,
    handleSelectItem,
    loading,
    error,
    sourceFiles,
    sortOrder,
    dataMode,
    mode,
    itemsPerPage,
    setItemsPerPage,
    setGlobalSortOrder,
    contextLoading,
    hasNextPage,
    fetchNextPage,
    pagination,
    pageScrollRef,
    detailContentRef,
    navigate,
    handleOpenFile,
    handleOpenSourceMenu,
    handleCloseSourceMenu,
  };
};
